import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, FileText, Sparkles, Building2, Check, Zap, Shield, Download } from "lucide-react";
import { useAuth } from "@/lib/auth";
import InstallApp from "@/components/InstallApp";
import PricingSection from "@/components/PricingSection";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Landing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const NAV = [
    { label: t("landing.navFeatures"), href: "#features" },
    { label: t("landing.navPricing"), href: "#pricing" },
    { label: t("landing.navAbout"), href: "#about" },
  ];
  const onSelectPlan = (planId, billing) => {
    if (planId === "free") return navigate(user ? "/app/oferte" : "/register");
    navigate(user ? `/app/abonament?plan=${planId}&billing=${billing}` : `/register?plan=${planId}`);
  };
  const onSelectAddon = (addonId, billing) => {
    navigate(user ? `/app/abonament?plan=${addonId}&billing=${billing}` : `/register?plan=${addonId}`);
  };
  const features = [
    { icon: Sparkles, title: t("landing.f1t"), desc: t("landing.f1d") },
    { icon: FileText, title: t("landing.f2t"), desc: t("landing.f2d") },
    { icon: Building2, title: t("landing.f3t"), desc: t("landing.f3d") },
    { icon: Shield, title: t("landing.f4t"), desc: t("landing.f4d") },
    { icon: Zap, title: t("landing.f5t"), desc: t("landing.f5d") },
    { icon: Check, title: t("landing.f6t"), desc: t("landing.f6d") },
  ];
  return (
    <div className="min-h-screen bg-[#0B111D] text-[#E2E8F0]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <img src="/brand/logo-icon.png" alt="IronOfferAI" className="w-9 h-9 rounded-lg shadow-sm"/>
            <span className="font-display font-extrabold text-xl tracking-tight">IronOfferAI</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            {NAV.map(n => (
              <a key={n.href} href={n.href} className="text-sm font-medium text-[#E2E8F0] hover:text-[#00BFFF] transition-colors">{n.label}</a>
            ))}
            <a href="#install" className="text-sm font-medium text-[#E2E8F0] hover:text-[#00BFFF] transition-colors flex items-center gap-1" data-testid="nav-install">
              <Download size={14}/> {t("landing.navApp")}
            </a>
          </nav>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            {user ? (
              <Link to="/app/oferte" className="btn-primary text-sm" data-testid="nav-dashboard">{t("landing.dashboard")}</Link>
            ) : (
              <>
                <Link to="/login" className="text-sm font-semibold hover:text-[#00BFFF] hidden sm:inline" data-testid="nav-login">{t("landing.login")}</Link>
                <Link to="/register" className="btn-primary text-sm" data-testid="nav-register">{t("landing.startFree")}</Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative grid-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="overline mb-6" data-testid="hero-overline">{t("landing.heroOverline")}</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95]">
              {t("landing.heroTitlePre")} <span className="text-[#00BFFF]">{t("landing.heroTitleHighlight")}</span> {t("landing.heroTitlePost")}
            </h1>
            <p className="mt-6 font-display text-xl sm:text-2xl font-extrabold tracking-tight" data-testid="hero-slogan">
              {t("landing.heroSlogan")}
              <span className="block text-[#00BFFF] text-base sm:text-lg mt-1">{t("landing.heroSloganSub")}</span>
            </p>
            <p className="mt-8 text-lg max-w-xl text-[#94A3B8] leading-relaxed">
              {t("landing.heroDesc")}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/register" className="btn-primary flex items-center gap-2 text-base" data-testid="hero-cta-register">
                {t("landing.heroCtaRegister")} <ArrowUpRight size={18}/>
              </Link>
              <a href="#pricing" className="btn-outline text-base">{t("landing.heroCtaPricing")}</a>
            </div>
            <div className="mt-12 flex items-center gap-8 text-sm text-[#94A3B8]">
              <div className="flex items-center gap-2"><Check size={16} className="text-[#22C55E]"/> {t("landing.heroCheck1")}</div>
              <div className="flex items-center gap-2"><Check size={16} className="text-[#22C55E]"/> {t("landing.heroCheck2")}</div>
            </div>
          </div>
          <div className="lg:col-span-5 fade-up delay-2">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-[#00BFFF]"/>
              <div className="relative card-flat p-6">
                <div className="overline mb-3">{t("landing.previewTitle")}</div>
                <div className="font-mono-plex text-xs text-[#94A3B8]">OF-202602-0001</div>
                <h3 className="font-display text-2xl font-bold mt-2">{t("landing.previewProject")}</h3>
                <div className="mt-5 space-y-2 text-sm">
                  <div className="flex justify-between border-b border-[#1E293B] py-2"><span>{t("landing.previewMaterials")}</span><span className="font-mono-plex">4.250,00 RON</span></div>
                  <div className="flex justify-between border-b border-[#1E293B] py-2"><span>{t("landing.previewLabor")}</span><span className="font-mono-plex">3.200,00 RON</span></div>
                  <div className="flex justify-between border-b border-[#1E293B] py-2"><span>{t("landing.previewTransport")}</span><span className="font-mono-plex">450,00 RON</span></div>
                </div>
                <div className="mt-4 bg-[#111C30] text-white p-4 flex justify-between items-center">
                  <span className="font-display font-bold">{t("landing.previewTotal")}</span>
                  <span className="font-display font-extrabold text-2xl">9.404,30 RON</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-[#1E293B] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="overline mb-4">{t("landing.navFeatures")}</div>
          <h2 className="font-display text-3xl lg:text-5xl font-extrabold tracking-tight max-w-3xl">{t("landing.featuresTitle")}</h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1E293B] border border-[#1E293B]">
            {features.map((f, i) => (
              <div key={i} className="bg-[#0B111D] p-8 hover:bg-[#0B111D] transition-colors">
                <f.icon size={28} className="text-[#00BFFF]" strokeWidth={1.5}/>
                <h3 className="font-display text-xl font-bold mt-4">{f.title}</h3>
                <p className="text-sm text-[#94A3B8] mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-[#0B111D] py-24 border-y border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="overline mb-4">{t("landing.navPricing")}</div>
            <h2 className="font-display text-3xl lg:text-5xl font-extrabold tracking-tight">{t("landing.pricingTitle")}</h2>
            <p className="mt-4 text-[#94A3B8]">{t("landing.pricingDesc")}</p>
          </div>
          <PricingSection user={user} onSelectPlan={onSelectPlan} onSelectAddon={onSelectAddon} />
        </div>
      </section>

      {/* About / CTA */}
      <section id="about" className="py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-display text-3xl lg:text-5xl font-extrabold tracking-tight">{t("landing.aboutTitle")}</h2>
          <p className="mt-6 text-lg text-[#94A3B8] max-w-2xl mx-auto">{t("landing.aboutDesc")}</p>
          <Link to="/register" className="btn-primary mt-10 inline-flex items-center gap-2 text-base">{t("landing.aboutCta")} <ArrowUpRight size={18}/></Link>
        </div>
      </section>

      {/* Install as App */}
      <section id="install" className="px-6 lg:px-12 pb-24">
        <div className="max-w-7xl mx-auto">
          <InstallApp variant="card"/>
        </div>
      </section>

      <footer className="border-t border-[#1E293B] py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="font-mono-plex text-xs text-[#94A3B8]">{t("landing.footerCopy")}</div>
          <div className="text-xs text-[#94A3B8]">{t("landing.footerTagline")}</div>
        </div>
      </footer>
    </div>
  );
}
